"use client";

import type React from "react";
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Box,
  Flex,
  IconButton,
  Text,
  VStack,
  Accordion,
  useToast,
  Alert,
  AlertIcon,
  Button,
} from "@chakra-ui/react";
import { ArrowLeft, Copy } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { RetellWebClient } from "retell-client-js-sdk";
import {
  agentService,
  type Agent,
  type VoiceDetails,
} from "../../voice-services/agentService";
import { VoiceSelect } from "./VoiceSelect";
import { LanguageSelect } from "./LanguageSelect";
import { callService } from "../../voice-services/callService";
import { WebhookSettings } from "./WebhookSettings";
import { SecuritySettings } from "./SecuritySettings";
import { CallSettings } from "./call-settings/CallSettings";
import { PostCallAnalysis } from "./post-call-analysis/PostCallAnalysis";
import { SpeechSettings } from "./speech-settings/SpeechSettings";
import { DynamicVariablesButton } from "./DynamicVariablesButton";
import { CustomLLMUrlInput } from "./CustomLLMUrlInput";
import { EditableAgentName } from "./EditableAgentName";

interface CustomLLMConfigProps {
  agent: Agent;
}

function getShortId(id?: string) {
  if (!id) return "";
  const len = id.length;
  return len > 5 ? `${id.slice(0, 2)}...${id.slice(-3)}` : id;
}

export const CustomLLMConfig: React.FC<CustomLLMConfigProps> = ({
  agent: initialAgent,
}) => {
  const navigate = useNavigate();
  const toast = useToast();
  const retellWebClientRef = useRef<RetellWebClient | null>(null);

  const [agent, setAgent] = useState<Agent>(initialAgent);
  const [isLoading, setIsLoading] = useState(false);
  const [voices, setVoices] = useState<VoiceDetails[]>([]);
  const [selectedVoice, setSelectedVoice] = useState<VoiceDetails | null>(null);
  const [isCallStarting, setIsCallStarting] = useState(false);
  const [callData, setCallData] = useState<{
    accessToken: string;
    callId: string;
  } | null>(null);
  const [isCallActive, setIsCallActive] = useState(false);
  const [dynamicVariables, setDynamicVariables] = useState<
    Record<string, string>
  >({});
  const [selectedVoiceState, setSelectedVoiceState] =
    useState<VoiceDetails | null>(null);

  // Safe websocket URL extraction for custom-llm
  const customWebsocketUrl =
    agent.response_engine?.type === "custom-llm"
      ? agent.response_engine.llm_websocket_url
      : "";

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        const voicesResponse = await agentService.listVoices();
        setVoices(voicesResponse);
        const foundVoice = voicesResponse.find(
          (v) => v.voice_id === agent.voice_id,
        );
        if (foundVoice) {
          setSelectedVoice(foundVoice);
          setSelectedVoiceState(foundVoice);
        }
      } catch (error) {
        console.error("Error loading data:", error);
        toast({
          title: "Error loading data",
          status: "error",
          duration: 3000,
        });
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, [agent.voice_id, toast]);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied to clipboard",
      status: "success",
      duration: 2000,
      isClosable: true,
    });
  };

  const initializeRetellWebClient = useCallback(() => {
    if (!retellWebClientRef.current) {
      retellWebClientRef.current = new RetellWebClient();
      retellWebClientRef.current.on("call_started", () => {
        setIsCallActive(true);
      });
      retellWebClientRef.current.on("call_ended", () => {
        setIsCallActive(false);
        setCallData(null);
      });
      retellWebClientRef.current.on("agent_start_talking", () => {
        // optional: analytics/logging
      });
      retellWebClientRef.current.on("agent_stop_talking", () => {
        // optional: analytics/logging
      });
      retellWebClientRef.current.on("error", (error: any) => {
        console.error("Call error:", error);
        toast({
          title: "Call Error",
          description:
            error?.response?.data?.message ||
            "An error occurred during the call",
          status: "error",
          duration: 5000,
        });
        retellWebClientRef.current?.stopCall();
      });
    }
  }, [toast]);

  const joinCall = useCallback(
    async (accessToken: string) => {
      if (!retellWebClientRef.current) {
        initializeRetellWebClient();
      }
      try {
        await retellWebClientRef.current?.startCall({
          accessToken,
          sampleRate: 24000,
        });
        toast({
          title: "Joined call successfully",
          status: "success",
          duration: 3000,
        });
      } catch (error: any) {
        console.error("Error joining call:", error);
        toast({
          title: "Error joining call",
          description:
            error instanceof Error
              ? error.message
              : "An unknown error occurred",
          status: "error",
          duration: 5000,
        });
      }
    },
    [initializeRetellWebClient, toast],
  );

  const handleStartCall = useCallback(async () => {
    setIsCallStarting(true);
    try {
      const callOptions: any = {};
      if (Object.keys(dynamicVariables).length > 0) {
        callOptions.retell_llm_dynamic_variables = dynamicVariables;
      }
      const response = await callService.createWebCall(
        agent.agent_id,
        callOptions,
      );
      const newCallData = {
        accessToken: response.access_token,
        callId: response.call_id,
      };
      setCallData(newCallData);
      toast({
        title: "Web call created",
        description: `Call ID: ${response.call_id}`,
        status: "success",
        duration: 5000,
      });
      await joinCall(response.access_token);
    } catch (error: any) {
      console.error("Error starting web call:", error);
      toast({
        title: "Error starting web call",
        description:
          error instanceof Error ? error.message : "An unknown error occurred",
        status: "error",
        duration: 5000,
      });
    } finally {
      setIsCallStarting(false);
    }
  }, [agent.agent_id, toast, joinCall, dynamicVariables]);

  const handleEndCall = useCallback(() => {
    retellWebClientRef.current?.stopCall();
    setIsCallActive(false);
    setCallData(null);
  }, []);

  const handleDynamicVariablesChange = useCallback(
    (vars: Record<string, string>) => {
      if (Object.keys(vars).length > 0) {
        console.log("Variables updated:", vars);
      }
      setDynamicVariables(vars);
    },
    [],
  );

  const testPanel = useMemo(
    () => (
      <VStack
        w="250px"
        p={4}
        borderLeft="1px solid"
        borderColor="gray.200"
        spacing={4}
        bg="white"
      >
        <Flex
          align="center"
          justify="space-between"
          w="full"
          bg="gray.100"
          rounded="md"
          p={2}
        >
          <Text fontWeight="semibold" color="gray.800">
            Test your agent
          </Text>
          <DynamicVariablesButton
            onVariablesChange={handleDynamicVariablesChange}
            prompt=""
            beginMessage=""
          />
        </Flex>

        <Box textAlign="center" py={8}>
          <Box as="span" fontSize="48px">
            🎙️
          </Box>
          <Button
            mt={4}
            w="full"
            onClick={isCallActive ? handleEndCall : handleStartCall}
            isLoading={isCallStarting}
            loadingText="Starting call..."
            colorScheme="primary"
            bg="primary.600"
            _hover={{ bg: "primary.700" }}
            variant="solid"
          >
            {isCallActive ? "End Call" : "Start Call"}
          </Button>
        </Box>
      </VStack>
    ),
    [
      isCallActive,
      handleEndCall,
      handleStartCall,
      isCallStarting,
      handleDynamicVariablesChange,
    ],
  );

  const mainContent = useMemo(
    () => (
      <Flex h="100vh" bg="gray.50" color="gray.800" direction="column">
        {/* Top Bar */}
        <Flex
          px={4}
          py={3}
          borderBottom="1px solid"
          borderColor="gray.200"
          direction="column"
          bg="white"
        >
          <Flex align="center">
            <IconButton
              aria-label="Back"
              icon={<ArrowLeft />}
              variant="ghost"
              color="gray.800"
              _hover={{ bg: "gray.200" }}
              onClick={() => navigate("/voice-agents")}
            />
            <Box ml={4}>
              <EditableAgentName
                agentId={agent.agent_id}
                initialName={agent.agent_name || ""}
              />
              <Text color="gray.600" fontSize="sm">
                Custom LLM &bull; Agent ID: {getShortId(agent.agent_id)}{" "}
                <IconButton
                  aria-label="Copy Agent ID"
                  icon={<Copy size={12} />}
                  size="xs"
                  variant="ghost"
                  onClick={() => copyToClipboard(agent.agent_id)}
                  color="gray.600"
                  _hover={{ bg: "gray.100" }}
                />
              </Text>
            </Box>
          </Flex>
        </Flex>

        {/* Voice / Language Row */}
        <Flex
          px={4}
          py={2}
          borderBottom="1px solid"
          borderColor="gray.200"
          gap={2}
          bg="white"
        >
          <VoiceSelect
            initialVoice={selectedVoice}
            voices={voices}
            agentId={agent.agent_id}
            setSelectedVoice={setSelectedVoice}
          />
          <LanguageSelect
            initialLanguage={agent.language}
            agentId={agent.agent_id}
          />
        </Flex>

        {/* Main Split */}
        <Flex flex={1} overflow="hidden" alignItems="stretch">
          {/* Left: LLM URL + Info */}
          <Box
            flex={1}
            p={4}
            borderRight="1px solid"
            borderColor="gray.200"
            bg="white"
          >
            <VStack align="stretch" h="full" spacing={4}>
              <CustomLLMUrlInput
                agentId={agent.agent_id}
                initialUrl={
                  customWebsocketUrl || "https://replace-with-your-llm-url.com"
                }
              />

              <Alert bg="transparent" status="info" rounded="md" mt={-2} p={0}>
                <AlertIcon color="gray.500" h={3} />
                <Text fontSize="xs" ml={-2} color="gray.600">
                  Fully customize response generation with your LLM.
                </Text>
              </Alert>
            </VStack>
          </Box>

          {/* Right: Settings rail */}
          <VStack
            w="300px"
            spacing={0}
            overflow="auto"
            p={4}
            borderRight="1px solid"
            borderColor="gray.200"
            bg="white"
            sx={{
              "&::-webkit-scrollbar": { width: "8px" },
              "&::-webkit-scrollbar-track": { backgroundColor: "white" },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "gray.300",
                borderRadius: "6px",
                border: "2px solid white",
              },
            }}
          >
            <Accordion allowMultiple w="full">
              <SpeechSettings agentId={agent.agent_id} />
              <CallSettings
                agentId={agent.agent_id}
                initialSettings={{
                  enable_voicemail_detection: agent.enable_voicemail_detection,
                  end_call_after_silence_ms: agent.end_call_after_silence_ms,
                  max_call_duration_ms: agent.max_call_duration_ms,
                  begin_message_delay_ms: agent.begin_message_delay_ms,
                }}
              />
              <PostCallAnalysis
                agentId={agent.agent_id}
                initialAnalysisData={agent.post_call_analysis_data ?? null}
              />
              <SecuritySettings
                agentId={agent.agent_id}
                initialOptOut={
                  agent.data_storage_setting === "basic_attributes_only"
                }
                initialFallbackVoiceIds={agent.fallback_voice_ids || null}
              />
              <WebhookSettings
                agentId={agent.agent_id}
                initialWebhookUrl={agent.agent_webhook_url}
              />
            </Accordion>
          </VStack>

          {/* Test panel */}
          {testPanel}
        </Flex>
      </Flex>
    ),
    [agent, voices, selectedVoice, navigate, testPanel, customWebsocketUrl],
  );

  return mainContent;
};
